import { type ChangeEvent, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { usePageTitle } from '@/features/pageTitle';
import {
  cancelAiDelayedDelivery,
  createAiTraceFeedback,
  getAiDelayedDelivery,
  getAiTrace,
  getAiTraces,
  sendAiDelayedDeliveryNow,
  type AiDelayedDeliveryDetailDto,
  type AiTraceDetailDto,
  type AiTraceFiltersDto,
  type AiTraceKnowledgeBaseSourceDto,
  type AiTraceListItemDto,
  type AiTraceModeDto,
  type AiTraceReasonCodeDto,
  type AiTraceStatusDto,
} from '@/shared/api/ai-trace';
import { PageContainer } from '@/shared/ui/PageContainer';
import { CustomTypography } from '@/shared/ui/Typography';

type FilterState = {
  company_id: string;
  channel_id: string;
  chat_id: string;
  source: string;
  mode: AiTraceModeDto | '';
  status: AiTraceStatusDto | '';
  reason_code: AiTraceReasonCodeDto | '';
  provider: string;
  limit: string;
};

const modeOptions: Array<{ value: AiTraceModeDto; label: string }> = [
  { value: 'conversation_insights', label: 'Аналитика диалога' },
  { value: 'draft_reply', label: 'Черновик ответа' },
  { value: 'autonomous_reply', label: 'Автоответ' },
  { value: 'campaign_draft', label: 'Черновик рассылки' },
  { value: 'crm_autofill', label: 'CRM autofill' },
  { value: 'operator_action', label: 'Действие оператора' },
  { value: 'unknown', label: 'Неизвестно' },
];

const statusOptions: Array<{ value: AiTraceStatusDto; label: string }> = [
  { value: 'completed', label: 'Завершен' },
  { value: 'drafted', label: 'Черновик' },
  { value: 'sent', label: 'Отправлен' },
  { value: 'scheduled', label: 'Отложен' },
  { value: 'skipped', label: 'Пропущен' },
  { value: 'failed', label: 'Ошибка' },
];

const reasonOptions: Array<{ value: AiTraceReasonCodeDto; label: string }> = [
  { value: 'completed', label: 'Завершено' },
  { value: 'draft_reply_generated', label: 'Черновик ответа создан' },
  { value: 'autonomous_reply_sent', label: 'Автоответ отправлен' },
  { value: 'autonomous_reply_scheduled', label: 'Автоответ отложен' },
  { value: 'campaign_draft_generated', label: 'Черновик рассылки создан' },
  { value: 'handoff_active', label: 'AI пропущен: оператор недавно ответил' },
  { value: 'module_disabled', label: 'AI пропущен: модуль выключен' },
  { value: 'channel_not_scoped', label: 'Канал вне scope AI' },
  { value: 'source_not_enabled', label: 'Источник выключен' },
  { value: 'policy_unavailable', label: 'Политика недоступна' },
  { value: 'policy_allowed', label: 'Политика разрешила ответ' },
  { value: 'delay_required', label: 'Требуется отложенная отправка' },
  { value: 'daily_limit_reached', label: 'Достигнут дневной лимит' },
  { value: 'dry_run', label: 'Dry run' },
  { value: 'drafts_disabled_by_mode', label: 'Черновики выключены режимом' },
  {
    value: 'autonomous_replies_global_disabled',
    label: 'Автоответы глобально выключены',
  },
  { value: 'draft_reply_not_supported', label: 'Черновик не поддержан' },
  { value: 'empty_draft', label: 'Пустой черновик' },
  { value: 'producer_unavailable', label: 'Producer недоступен' },
  { value: 'delayed_queue_unavailable', label: 'Очередь отложенных недоступна' },
  { value: 'invalid_not_before', label: 'Некорректный not_before' },
  {
    value: 'duplicate_policy_reservation',
    label: 'Дубликат policy reservation',
  },
  { value: 'request_in_progress', label: 'Запрос уже выполняется' },
  { value: 'already_processed', label: 'Уже обработано' },
  { value: 'rate_limited', label: 'Rate limit' },
  { value: 'provider_failure', label: 'Ошибка AI-провайдера' },
  {
    value: 'delayed_delivery_send_now_sent',
    label: 'Отложенный ответ отправлен оператором',
  },
  {
    value: 'delayed_delivery_send_now_failed',
    label: 'Отправка отложенного ответа не удалась',
  },
  { value: 'delayed_delivery_canceled', label: 'Отложенный ответ отменен' },
  {
    value: 'delayed_delivery_cancel_failed',
    label: 'Отмена отложенного ответа не удалась',
  },
  { value: 'unknown', label: 'Неизвестно' },
];

const reasonLabels: Record<AiTraceReasonCodeDto, string> = {
  ...Object.fromEntries(
    reasonOptions.map((item) => [item.value, item.label])
  ) as Record<AiTraceReasonCodeDto, string>,
  handoff_active: 'AI пропущен: оператор недавно ответил',
};

const initialFilters: FilterState = {
  company_id: '',
  channel_id: '',
  chat_id: '',
  source: '',
  mode: '',
  status: '',
  reason_code: '',
  provider: '',
  limit: '50',
};

const modeValues = new Set(modeOptions.map((item) => item.value));
const statusValues = new Set(statusOptions.map((item) => item.value));
const reasonValues = new Set(reasonOptions.map((item) => item.value));

const metadataMessageKeys = [
  'message',
  'source_message',
  'incoming_message',
  'incomingMessage',
  'sourceMessage',
  'text',
  'input',
  'prompt',
];

const metadataReplyKeys = [
  'reply',
  'reply_text',
  'replyText',
  'draft',
  'candidate',
  'candidate_reply',
  'candidateReply',
  'final_reply',
  'finalReply',
  'output',
  'answer',
];

const delayedIdKeys = [
  'deliveryId',
  'delayed_delivery_id',
  'delivery_id',
  'delayedDeliveryId',
];

function getReasonLabel(
  trace: Pick<AiTraceListItemDto, 'reason_code' | 'reason_text_key'>
) {
  if (trace.reason_code && reasonLabels[trace.reason_code]) {
    return reasonLabels[trace.reason_code];
  }

  return trace.reason_text_key || trace.reason_code || 'Причина не указана';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return null;
}

function stringifyCompact(value: unknown) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);

  return JSON.stringify(value);
}

function firstMetadataValue(
  metadata: Record<string, unknown> | undefined,
  keys: string[]
): string | null {
  if (!metadata) return null;

  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) return value;
    const record = asRecord(value);
    if (record) {
      const nested: string | null = firstMetadataValue(record, keys);
      if (nested) return nested;
    }
  }

  return null;
}

function getDelayedDeliveryId(trace: AiTraceListItemDto | AiTraceDetailDto) {
  const metadata = 'raw_metadata' in trace ? trace.raw_metadata : undefined;

  return firstMetadataValue(metadata, delayedIdKeys);
}

function formatDate(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatUsage(trace: AiTraceListItemDto) {
  const usage = trace.usage;
  const totalTokens =
    typeof usage?.total_tokens === 'number'
      ? usage.total_tokens
      : (usage?.input_tokens ?? 0) + (usage?.output_tokens ?? 0);
  const cost = usage?.estimated_cost_usd;
  const parts = [
    trace.latency_ms !== null ? `${trace.latency_ms} ms` : null,
    totalTokens ? `${totalTokens} tok` : null,
    typeof cost === 'number' ? `$${cost.toFixed(4)}` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' / ') : '—';
}

function modeLabel(mode: AiTraceModeDto) {
  return modeOptions.find((item) => item.value === mode)?.label ?? mode;
}

function statusLabel(status: AiTraceStatusDto) {
  return statusOptions.find((item) => item.value === status)?.label ?? status;
}

function deliveryLabel(trace: AiTraceListItemDto) {
  const state = trace.delivery_state?.state;

  return state ? String(state) : '—';
}

function normalizeLimit(value: string) {
  const parsedLimit = Number(value);

  return Number.isFinite(parsedLimit) && parsedLimit >= 1
    ? Math.min(Math.floor(parsedLimit), 200)
    : 50;
}

function pickSearchParam(params: URLSearchParams, key: keyof FilterState) {
  return params.get(key)?.trim() || '';
}

function parseInitialFilters(search: string): FilterState {
  const params = new URLSearchParams(search);
  const mode = pickSearchParam(params, 'mode');
  const status = pickSearchParam(params, 'status');
  const reasonCode = pickSearchParam(params, 'reason_code');
  const limitParam = pickSearchParam(params, 'limit');

  return {
    ...initialFilters,
    company_id: pickSearchParam(params, 'company_id'),
    channel_id: pickSearchParam(params, 'channel_id'),
    chat_id: pickSearchParam(params, 'chat_id'),
    source: pickSearchParam(params, 'source'),
    mode: modeValues.has(mode as AiTraceModeDto)
      ? (mode as AiTraceModeDto)
      : '',
    status: statusValues.has(status as AiTraceStatusDto)
      ? (status as AiTraceStatusDto)
      : '',
    reason_code: reasonValues.has(reasonCode as AiTraceReasonCodeDto)
      ? (reasonCode as AiTraceReasonCodeDto)
      : '',
    provider: pickSearchParam(params, 'provider'),
    limit: limitParam
      ? String(normalizeLimit(limitParam))
      : initialFilters.limit,
  };
}

function getInitialFilters(): FilterState {
  if (typeof window === 'undefined') return initialFilters;

  return parseInitialFilters(window.location.search);
}

function buildQueryFilters(filters: FilterState): AiTraceFiltersDto {
  const limit = normalizeLimit(filters.limit);

  return {
    company_id: filters.company_id || undefined,
    channel_id: filters.channel_id || undefined,
    chat_id: filters.chat_id || undefined,
    source: filters.source || undefined,
    mode: filters.mode || undefined,
    status: filters.status || undefined,
    reason_code: filters.reason_code || undefined,
    limit,
  };
}

function canSendDelayedNow(delivery?: AiDelayedDeliveryDetailDto) {
  return Boolean(
    delivery && ['pending', 'blocked', 'failed'].includes(delivery.status)
  );
}

function canCancelDelayed(delivery?: AiDelayedDeliveryDetailDto) {
  return Boolean(delivery && ['pending', 'processing'].includes(delivery.status));
}

function DetailBlock({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Stack spacing="6px">
      <CustomTypography variant="subtitle2">{title}</CustomTypography>
      <Box
        sx={{
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          p: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}

export const AiTracePage = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<FilterState>(() =>
    getInitialFilters()
  );
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

  usePageTitle('Журнал ИИ');

  const queryFilters = useMemo(() => buildQueryFilters(filters), [filters]);
  const tracesQuery = useQuery({
    queryKey: ['ai-trace', 'list', queryFilters],
    queryFn: () => getAiTraces(queryFilters),
  });

  const rows = useMemo(() => {
    const items = tracesQuery.data?.items ?? [];
    const provider = filters.provider.trim().toLowerCase();

    if (!provider) return items;

    return items.filter((item) =>
      (item.provider ?? '').toLowerCase().includes(provider)
    );
  }, [filters.provider, tracesQuery.data?.items]);

  const detailQuery = useQuery({
    queryKey: ['ai-trace', 'detail', selectedTraceId],
    queryFn: () => getAiTrace(selectedTraceId!),
    enabled: selectedTraceId !== null,
  });
  const selectedTrace = detailQuery.data;
  const delayedDeliveryId = selectedTrace
    ? getDelayedDeliveryId(selectedTrace)
    : null;
  const delayedQuery = useQuery({
    queryKey: ['ai-trace', 'delayed-delivery', delayedDeliveryId],
    queryFn: () => getAiDelayedDelivery(delayedDeliveryId!),
    enabled: delayedDeliveryId !== null,
  });

  const invalidateTraceQueries = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['ai-trace', 'list'] }),
      queryClient.invalidateQueries({
        queryKey: ['ai-trace', 'detail', selectedTraceId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['ai-trace', 'delayed-delivery', delayedDeliveryId],
      }),
    ]);

  const sendNowMutation = useMutation({
    mutationFn: () => sendAiDelayedDeliveryNow(delayedDeliveryId!),
    onSuccess: invalidateTraceQueries,
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelAiDelayedDelivery(delayedDeliveryId!),
    onSuccess: invalidateTraceQueries,
  });
  const feedbackMutation = useMutation({
    mutationFn: (rating: 1 | -1) =>
      createAiTraceFeedback({
        target_type: 'trace',
        target_id: selectedTrace!.id,
        chat_id: selectedTrace!.chat_id!,
        rating,
      }),
    onSuccess: () => setFeedbackMessage('Оценка сохранена'),
    onError: () => setFeedbackMessage('Не удалось сохранить оценку'),
  });

  const updateFilter =
    (key: keyof FilterState) => (event: ChangeEvent<HTMLInputElement>) => {
      setFilters((current) => ({ ...current, [key]: event.target.value }));
    };

  const openDrawer = (traceId: string) => {
    setSelectedTraceId(traceId);
    setFeedbackMessage(null);
  };

  const closeDrawer = () => {
    setSelectedTraceId(null);
    setFeedbackMessage(null);
  };

  const incomingMessage = firstMetadataValue(
    selectedTrace?.raw_metadata,
    metadataMessageKeys
  );
  const replyMessage = firstMetadataValue(
    selectedTrace?.raw_metadata,
    metadataReplyKeys
  );
  const kbSources: AiTraceKnowledgeBaseSourceDto[] = selectedTrace
    ?.knowledge_base_sources.length
    ? selectedTrace.knowledge_base_sources
    : (selectedTrace?.knowledge_base_source_ids ?? []).map((id) => ({
        id,
        title: id,
        tags: [],
      }));
  const delayedDelivery = delayedQuery.data;
  const delayedMutationPending =
    sendNowMutation.isPending || cancelMutation.isPending;
  const sendNowDisabled =
    !canSendDelayedNow(delayedDelivery) || delayedMutationPending;
  const cancelDisabled =
    !canCancelDelayed(delayedDelivery) || delayedMutationPending;

  return (
    <PageContainer>
      <Stack spacing={2.5}>
        <Stack spacing={0.5}>
          <CustomTypography component="h1" variant="h5">
            Журнал ИИ
          </CustomTypography>
          <CustomTypography color="text.secondary" variant="body2">
            Trace решений, ответов и отложенных AI-доставок по каналам и
            диалогам.
          </CustomTypography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
          <Stack
            direction="row"
            flexWrap="wrap"
            gap={1.5}
            sx={{ '& .MuiTextField-root': { minWidth: 160 } }}
          >
            <TextField
              size="small"
              label="Company ID"
              value={filters.company_id}
              onChange={updateFilter('company_id')}
            />
            <TextField
              size="small"
              label="Channel ID"
              value={filters.channel_id}
              onChange={updateFilter('channel_id')}
            />
            <TextField
              size="small"
              label="Chat ID"
              value={filters.chat_id}
              onChange={updateFilter('chat_id')}
            />
            <TextField
              size="small"
              label="Source"
              value={filters.source}
              onChange={updateFilter('source')}
            />
            <TextField
              select
              size="small"
              label="Mode"
              value={filters.mode}
              onChange={updateFilter('mode')}
            >
              <MenuItem value="">Все</MenuItem>
              {modeOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Status"
              value={filters.status}
              onChange={updateFilter('status')}
            >
              <MenuItem value="">Все</MenuItem>
              {statusOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Reason"
              value={filters.reason_code}
              onChange={updateFilter('reason_code')}
            >
              <MenuItem value="">Все</MenuItem>
              {reasonOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Provider"
              value={filters.provider}
              onChange={updateFilter('provider')}
              helperText="Фильтр на клиенте"
            />
            <TextField
              size="small"
              label="Limit"
              type="number"
              value={filters.limit}
              onChange={updateFilter('limit')}
              inputProps={{ min: 1, max: 200 }}
            />
          </Stack>
        </Paper>

        {tracesQuery.isError ? (
          <Alert severity="error">Не удалось загрузить журнал ИИ.</Alert>
        ) : null}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: '8px' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Время</TableCell>
                <TableCell>Диалог / клиент</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Причина</TableCell>
                <TableCell>Provider / model</TableCell>
                <TableCell>Latency / tokens / cost</TableCell>
                <TableCell>Delivery</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tracesQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={9}>Загружаем trace...</TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9}>Trace не найден.</TableCell>
                </TableRow>
              ) : (
                rows.map((trace) => (
                  <TableRow key={trace.id} hover>
                    <TableCell>{formatDate(trace.created_at)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <span>{trace.chat_id ?? 'chat —'}</span>
                        <CustomTypography
                          color="text.secondary"
                          variant="caption"
                        >
                          {trace.company_id ?? 'company —'} /{' '}
                          {trace.channel_id ?? 'channel —'}
                        </CustomTypography>
                        {trace.channel_id && trace.chat_id ? (
                          <Link
                            href={`/chats?channel_id=${encodeURIComponent(
                              trace.channel_id
                            )}&chat_id=${encodeURIComponent(trace.chat_id)}`}
                            underline="hover"
                          >
                            Открыть диалог
                          </Link>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{modeLabel(trace.mode)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={statusLabel(trace.status)} />
                    </TableCell>
                    <TableCell>{getReasonLabel(trace)}</TableCell>
                    <TableCell>
                      {trace.provider ?? '—'}
                      {trace.model ? ` / ${trace.model}` : ''}
                    </TableCell>
                    <TableCell>{formatUsage(trace)}</TableCell>
                    <TableCell>{deliveryLabel(trace)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openDrawer(trace.id)}
                      >
                        Детали
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Drawer
        anchor="right"
        open={selectedTraceId !== null}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2.5 } }}
      >
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Box>
              <CustomTypography component="h2" variant="h6">
                Trace details
              </CustomTypography>
              <CustomTypography color="text.secondary" variant="caption">
                {selectedTraceId}
              </CustomTypography>
            </Box>
            <Button size="small" onClick={closeDrawer}>
              Закрыть
            </Button>
          </Stack>

          {detailQuery.isLoading ? (
            <CustomTypography>Загружаем детали...</CustomTypography>
          ) : detailQuery.isError || !selectedTrace ? (
            <Alert severity="error">Не удалось загрузить детали trace.</Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={modeLabel(selectedTrace.mode)} />
                <Chip size="small" label={statusLabel(selectedTrace.status)} />
                <Chip size="small" label={getReasonLabel(selectedTrace)} />
              </Stack>

              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!selectedTrace.chat_id || feedbackMutation.isPending}
                    onClick={() => feedbackMutation.mutate(1)}
                  >
                    Корректно
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={!selectedTrace.chat_id || feedbackMutation.isPending}
                    onClick={() => feedbackMutation.mutate(-1)}
                  >
                    Некорректно
                  </Button>
                </Stack>
                {feedbackMessage ? (
                  <CustomTypography color="text.secondary" variant="caption">
                    {feedbackMessage}
                  </CustomTypography>
                ) : null}
              </Stack>

              <DetailBlock title="Входящее / source message">
                {incomingMessage ?? 'Нет message-поля в metadata.'}
              </DetailBlock>

              <DetailBlock title="Candidate / final reply">
                {replyMessage ?? 'Нет reply-поля в metadata.'}
              </DetailBlock>

              <DetailBlock title="KB sources">
                {kbSources.length ? (
                  <Stack spacing={0.5}>
                    {kbSources.map((source) => (
                      <span key={source.id}>
                        {source.title || source.id}
                        {source.tags.length
                          ? ` · ${source.tags.join(', ')}`
                          : ''}
                      </span>
                    ))}
                  </Stack>
                ) : (
                  'Источники не указаны.'
                )}
              </DetailBlock>

              <DetailBlock title="Provider usage / retries / errors">
                <Stack spacing={0.5}>
                  <span>
                    Provider: {selectedTrace.provider ?? '—'} /{' '}
                    {selectedTrace.model ?? '—'}
                  </span>
                  <span>Usage: {stringifyCompact(selectedTrace.usage)}</span>
                  <span>Retries: {selectedTrace.retry_count ?? 0}</span>
                  <span>Error: {selectedTrace.error ?? '—'}</span>
                </Stack>
              </DetailBlock>

              <DetailBlock title="Policy / delivery state">
                {stringifyCompact(selectedTrace.delivery_state)}
              </DetailBlock>

              {delayedDeliveryId ? (
                <DetailBlock title="Отложенный ответ">
                  {delayedQuery.isLoading ? (
                    'Загружаем delayed delivery...'
                  ) : delayedQuery.isError ? (
                    'Не удалось загрузить delayed delivery.'
                  ) : delayedDelivery ? (
                    <Stack spacing={1}>
                      <Chip
                        size="small"
                        label={`status: ${delayedDelivery.status}`}
                        sx={{ alignSelf: 'flex-start' }}
                      />
                      <Box>{delayedDelivery.message}</Box>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={sendNowDisabled}
                          onClick={() => sendNowMutation.mutate()}
                        >
                          Отправить сейчас
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={cancelDisabled}
                          onClick={() => cancelMutation.mutate()}
                        >
                          Отменить
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    'Delayed delivery не найден.'
                  )}
                </DetailBlock>
              ) : null}

              <Divider />

              <DetailBlock title="Raw IDs / debug">
                <Stack spacing={0.5}>
                  <span>trace_id: {selectedTrace.id}</span>
                  <span>
                    source_message_id: {selectedTrace.source_message_id ?? '—'}
                  </span>
                  <span>
                    outbound_message_id:{' '}
                    {selectedTrace.outbound_message_id ?? '—'}
                  </span>
                  <span>delayed_delivery_id: {delayedDeliveryId ?? '—'}</span>
                  <span>
                    raw_metadata: {stringifyCompact(selectedTrace.raw_metadata)}
                  </span>
                </Stack>
              </DetailBlock>
            </>
          )}
        </Stack>
      </Drawer>
    </PageContainer>
  );
};
